import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  ClipboardList, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  CalendarIcon,
  CheckCircle2,
  Circle,
  AlertTriangle,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  ArrowUpDown,
  MessageSquare,
  Send,
  X,
  Search,
  Trash,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { API } from "../config/api";
import { ClientSelect } from "../components/ClientSelect";
import { CaseSelect } from "../components/CaseSelect";
import { useDataCache } from "../context/DataCacheContext";

const PRIORITY_CONFIG = {
  high: { label: "Înaltă", color: "bg-red-500/10 text-red-600 border-red-200", icon: ArrowUp },
  normal: { label: "Normală", color: "bg-blue-500/10 text-blue-600 border-blue-200", icon: ArrowRight },
  low: { label: "Scăzută", color: "bg-gray-500/10 text-gray-600 border-gray-200", icon: ArrowDown },
};

export default function AdminNotesPage() {
  const { getAuthHeader } = useAuth();
  const { fetchClients: fetchCachedClients, fetchCases: fetchCachedCases, fetchCasesBC: fetchCachedCasesBC } = useDataCache();
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [casesBC, setCasesBC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    content: "",
    case_id: "",
    priority: "normal",
    due_date: null,
  });
  const [filter, setFilter] = useState("all"); // all, active, completed
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default"); // "default", "deadline", "case"
  const [filterPriority, setFilterPriority] = useState("all"); // "all", "high", "normal", "low"
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [massDeleteMode, setMassDeleteMode] = useState(false);
  const [deletingCompleted, setDeletingCompleted] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      const [notesRes, clientsData, casesData, casesBCData] = await Promise.all([
        axios.get(`${API}/admin-notes`, getAuthHeader()),
        fetchCachedClients(),
        fetchCachedCases(),
        fetchCachedCasesBC(),
      ]);
      setNotes(notesRes.data);
      setClients(clientsData);
      setCases(casesData);
      setCasesBC(casesBCData);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, fetchCachedClients, fetchCachedCases, fetchCachedCasesBC]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleOpenDialog = (note = null) => {
    if (note) {
      setSelectedNote(note);
      setFormData({
        content: note.content,
        priority: note.priority,
        due_date: note.due_date ? new Date(note.due_date) : null,
        client_id: note.client_id || "",
        client_name: note.client_name || "",
        case_id: note.case_id || "",
      });
    } else {
      setSelectedNote(null);
      setFormData({ client_id: "", client_name: "", content: "", case_id: "", priority: "normal", due_date: null });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.client_name.trim()) {
      toast.error("Clientul este obligatoriu");
      return;
    }

    try {
      const payload = {
        content: formData.content,
        priority: formData.priority,
        due_date: formData.due_date ? format(formData.due_date, "yyyy-MM-dd") : null,
        client_id: formData.client_id || null,
        client_name: formData.client_name || null,
        case_id: formData.case_id || null,
      };

      if (selectedNote) {
        await axios.put(`${API}/admin-notes/${selectedNote.id}`, payload, getAuthHeader());
        toast.success("Notă actualizată");
      } else {
        await axios.post(`${API}/admin-notes`, payload, getAuthHeader());
        toast.success("Notă adăugată");
      }
      
      setDialogOpen(false);
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Eroare la salvarea notei");
    }
  };

  const handleToggleComplete = async (note) => {
    try {
      await axios.patch(`${API}/admin-notes/${note.id}/toggle`, {}, getAuthHeader());
      fetchNotes();
      toast.success(note.is_completed ? "Marcat ca necompletat" : "Marcat ca completat");
    } catch (error) {
      console.error("Error toggling note:", error);
      toast.error("Eroare la actualizarea notei");
    }
  };

  const handleDelete = async () => {
    if (!selectedNote) return;
    
    try {
      await axios.delete(`${API}/admin-notes/${selectedNote.id}`, getAuthHeader());
      toast.success("Notă ștearsă");
      setDeleteDialogOpen(false);
      setSelectedNote(null);
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Eroare la ștergerea notei");
    }
  };

  const handleAddComment = async () => {
    if (!selectedNote || !newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const response = await axios.post(
        `${API}/admin-notes/${selectedNote.id}/comments`,
        { text: newComment.trim() },
        getAuthHeader()
      );
      setSelectedNote(response.data);
      setNewComment("");
      fetchNotes();
      toast.success("Notiță adăugată");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Eroare la adăugarea notiței");
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedNote) return;
    
    try {
      const response = await axios.delete(
        `${API}/admin-notes/${selectedNote.id}/comments/${commentId}`,
        getAuthHeader()
      );
      setSelectedNote(response.data);
      fetchNotes();
      toast.success("Notiță ștearsă");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Eroare la ștergerea notiței");
    }
  };

  const handleOpenViewDialog = (note) => {
    setSelectedNote(note);
    setViewDialogOpen(true);
  };

  // Days info helper
  const getDaysInfo = (dueDateStr, isCompleted) => {
    if (!dueDateStr || isCompleted) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDateStr);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { type: "overdue", days: Math.abs(diffDays) };
    if (diffDays === 0) return { type: "today", days: 0 };
    if (diffDays <= 3) return { type: "soon", days: diffDays };
    return { type: "ok", days: diffDays };
  };

  // Normalize for diacritic-insensitive search
  const normalizeSearch = (str) => (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  // Delete all completed
  const handleDeleteAllCompleted = async () => {
    const completedNotes = notes.filter(n => n.is_completed);
    if (completedNotes.length === 0) return;
    setDeletingCompleted(true);
    try {
      await Promise.all(completedNotes.map(n => 
        axios.delete(`${API}/admin-notes/${n.id}`, getAuthHeader())
      ));
      toast.success(`${completedNotes.length} sarcini completate șterse`);
      fetchNotes();
    } catch (error) {
      toast.error("Eroare la ștergerea sarcinilor");
    } finally {
      setDeletingCompleted(false);
    }
  };

  // Mass delete selected
  const handleMassDelete = async () => {
    if (selectedForDelete.length === 0) return;
    try {
      await Promise.all(selectedForDelete.map(id => 
        axios.delete(`${API}/admin-notes/${id}`, getAuthHeader())
      ));
      toast.success(`${selectedForDelete.length} sarcini șterse`);
      setSelectedForDelete([]);
      setMassDeleteMode(false);
      fetchNotes();
    } catch (error) {
      toast.error("Eroare la ștergerea sarcinilor");
    }
  };

  const toggleSelectForDelete = (noteId) => {
    setSelectedForDelete(prev => 
      prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId]
    );
  };

  const filteredNotes = notes.filter(note => {
    if (filter === "active") return !note.is_completed;
    if (filter === "completed") return note.is_completed;
    return true;
  }).filter(note => {
    if (filterPriority !== "all") return note.priority === filterPriority;
    return true;
  }).filter(note => {
    if (!searchQuery.trim()) return true;
    const q = normalizeSearch(searchQuery);
    return normalizeSearch(note.content).includes(q) || normalizeSearch(note.client_name).includes(q);
  });

  const sortedNotes = [...filteredNotes].sort((a, b) => {
    // Completed notes at the bottom
    if (a.is_completed !== b.is_completed) return a.is_completed ? 1 : -1;
    
    // Explicit sort modes
    if (sortBy === "deadline") {
      // Notes with deadline first, sorted by date asc
      if (a.due_date && b.due_date) return new Date(a.due_date) - new Date(b.due_date);
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    }
    if (sortBy === "case") {
      return (a.case_number || "zzz").localeCompare(b.case_number || "zzz", 'ro');
    }
    
    // Default: priority then deadline then created_at
    const priorityOrder = { high: 0, normal: 1, low: 2 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    if (a.due_date && b.due_date) return new Date(a.due_date) - new Date(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const stats = {
    total: notes.length,
    active: notes.filter(n => !n.is_completed).length,
    completed: notes.filter(n => n.is_completed).length,
    overdue: notes.filter(n => !n.is_completed && n.due_date && new Date(n.due_date) < new Date()).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notes-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Sarcini Personale
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Gestionează sarcinile și notele tale personale
          </p>
        </div>
        <div className="flex gap-2">
          {notes.filter(n => n.is_completed).length > 0 && !massDeleteMode && (
            <Button 
              variant="outline" 
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDeleteAllCompleted}
              disabled={deletingCompleted}
              data-testid="delete-completed-btn"
            >
              {deletingCompleted ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash className="w-4 h-4 mr-2" />}
              Șterge Completate ({notes.filter(n => n.is_completed).length})
            </Button>
          )}
          {!massDeleteMode ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { setMassDeleteMode(true); setSelectedForDelete([]); }}
              data-testid="mass-delete-mode-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Selectează pentru Ștergere
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handleMassDelete}
                disabled={selectedForDelete.length === 0}
                data-testid="mass-delete-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Șterge ({selectedForDelete.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setMassDeleteMode(false); setSelectedForDelete([]); }}
              >
                Anulează
              </Button>
            </div>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-note-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Sarcină
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-colors ${filter === 'all' ? 'ring-2 ring-primary' : 'hover:bg-secondary/50'}`} onClick={() => setFilter("all")}>
          <CardContent className="p-4">
            <p className="font-body text-sm text-muted-foreground">Total</p>
            <p className="font-heading text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${filter === 'active' ? 'ring-2 ring-blue-500' : 'hover:bg-secondary/50'}`} onClick={() => setFilter("active")}>
          <CardContent className="p-4">
            <p className="font-body text-sm text-muted-foreground">Active</p>
            <p className="font-heading text-2xl font-bold text-blue-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${filter === 'completed' ? 'ring-2 ring-green-500' : 'hover:bg-secondary/50'}`} onClick={() => setFilter("completed")}>
          <CardContent className="p-4">
            <p className="font-body text-sm text-muted-foreground">Completate</p>
            <p className="font-heading text-2xl font-bold text-green-600">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="font-body text-sm text-muted-foreground">Întârziate</p>
            <p className="font-heading text-2xl font-bold text-red-600">{stats.overdue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filter + Sort Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută în sarcini..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="notes-search-input"
          />
        </div>
        {[
          { key: "all", label: "Toate" },
          { key: "active", label: "Active" },
          { key: "completed", label: "Completate" },
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            data-testid={`filter-notes-${f.key}-btn`}
          >
            {f.label}
          </Button>
        ))}

        <div className="h-6 w-px bg-border" />

        {/* Sort buttons */}
        <Button
          variant={sortBy === "default" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("default")}
          data-testid="sort-notes-default-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Implicit
        </Button>
        <Button
          variant={sortBy === "deadline" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("deadline")}
          data-testid="sort-notes-deadline-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Termen
        </Button>
        <Button
          variant={sortBy === "case" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("case")}
          data-testid="sort-notes-case-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Dosar
        </Button>

        <div className="h-6 w-px bg-border" />

        {/* Priority filter */}
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-40" data-testid="filter-priority-select">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Prioritate" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate prioritățile</SelectItem>
            <SelectItem value="high">
              <div className="flex items-center gap-2">
                <ArrowUp className="w-3.5 h-3.5 text-red-600" />
                Înaltă
              </div>
            </SelectItem>
            <SelectItem value="normal">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-3.5 h-3.5 text-blue-600" />
                Normală
              </div>
            </SelectItem>
            <SelectItem value="low">
              <div className="flex items-center gap-2">
                <ArrowDown className="w-3.5 h-3.5 text-gray-600" />
                Scăzută
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      </div>{/* End Sticky */}

      {/* Notes List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            {filter === "all" ? "Toate Sarcinile" : filter === "active" ? "Sarcini Active" : "Sarcini Completate"}
            <Badge variant="secondary" className="ml-2">{sortedNotes.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedNotes.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-body text-muted-foreground">
                {searchQuery.trim() ? "Nu s-au găsit rezultate." :
                  filter === "all" 
                    ? "Nu ai sarcini. Adaugă prima ta sarcină!" 
                    : filter === "active" 
                      ? "Nu ai sarcini active."
                      : "Nu ai sarcini completate."}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-1.5">
                {sortedNotes.map((note) => {
                  const isOverdue = !note.is_completed && note.due_date && new Date(note.due_date) < new Date();
                  const daysInfo = getDaysInfo(note.due_date, note.is_completed);
                  
                  let borderClass = "border-l-[3px] ";
                  let statusLabel = "";
                  let statusBadgeClass = "";
                  if (note.is_completed) {
                    borderClass += "border-l-green-500 bg-green-50/30 opacity-60";
                    statusLabel = "Completat";
                    statusBadgeClass = "bg-green-100 text-green-700";
                  } else if (isOverdue) {
                    borderClass += "border-l-red-600 bg-red-50/40";
                    statusLabel = `Întârziat ${daysInfo?.days || ""}z`;
                    statusBadgeClass = "bg-red-100 text-red-700";
                  } else if (daysInfo?.type === "today") {
                    borderClass += "border-l-orange-500 bg-orange-50/30";
                    statusLabel = "Azi";
                    statusBadgeClass = "bg-orange-100 text-orange-700";
                  } else if (daysInfo?.type === "soon") {
                    borderClass += "border-l-yellow-500 bg-yellow-50/30";
                    statusLabel = `${daysInfo.days}z rămase`;
                    statusBadgeClass = "bg-yellow-100 text-yellow-700";
                  } else if (note.priority === "high") {
                    borderClass += "border-l-red-400";
                    statusLabel = "Prioritate înaltă";
                    statusBadgeClass = "bg-red-100 text-red-700";
                  } else if (note.priority === "normal") {
                    borderClass += "border-l-blue-500";
                    statusLabel = "În lucru";
                    statusBadgeClass = "bg-blue-100 text-blue-700";
                  } else {
                    borderClass += "border-l-gray-300";
                    statusLabel = "Prioritate scăzută";
                    statusBadgeClass = "bg-gray-100 text-gray-600";
                  }
                  
                  return (
                    <div
                      key={note.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all cursor-pointer hover:bg-secondary/30 ${borderClass} ${
                        selectedForDelete.includes(note.id) ? "ring-2 ring-red-400 bg-red-50/50" : ""
                      }`}
                      data-testid={`note-item-${note.id}`}
                      onClick={() => !massDeleteMode && handleOpenViewDialog(note)}
                    >
                      {massDeleteMode ? (
                        <Checkbox
                          checked={selectedForDelete.includes(note.id)}
                          onCheckedChange={() => toggleSelectForDelete(note.id)}
                          className="shrink-0"
                          data-testid={`mass-delete-checkbox-${note.id}`}
                        />
                      ) : (
                        <div className="shrink-0 cursor-pointer" onClick={(e) => { e.stopPropagation(); handleToggleComplete(note); }}>
                          {note.is_completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-muted-foreground hover:text-primary" />
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                        {/* Client */}
                        <span className={`font-body text-sm font-medium truncate ${note.is_completed ? "line-through text-muted-foreground" : ""}`}>
                          {note.client_name || note.content || "—"}
                        </span>
                        {/* Dosar */}
                        {note.case_number && (
                          <>
                            <span className="text-muted-foreground shrink-0">|</span>
                            <Badge 
                              variant="outline" 
                              className="font-mono text-[10px] px-1 py-0 cursor-pointer hover:bg-primary hover:text-primary-foreground shrink-0"
                              onClick={(e) => { e.stopPropagation(); if (note.case_id) { window.location.href = `/cases?open=${note.case_id}`; } }}
                            >
                              {note.case_number}
                            </Badge>
                          </>
                        )}
                        {/* Termen */}
                        <span className="text-muted-foreground shrink-0">|</span>
                        <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                          {note.due_date ? format(new Date(note.due_date), "dd/MM/yyyy", { locale: ro }) : "Fără termen"}
                        </span>
                        {/* Stadiu */}
                        <span className="text-muted-foreground shrink-0">|</span>
                        <Badge className={`text-[10px] px-1.5 py-0 shrink-0 ${statusBadgeClass}`}>{statusLabel}</Badge>
                        {/* Note count */}
                        {note.comments?.length > 0 && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 shrink-0">
                            <MessageSquare className="w-3 h-3" />{note.comments.length}
                          </span>
                        )}
                      </div>
                      {!massDeleteMode && (
                        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenDialog(note)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => { setSelectedNote(note); setDeleteDialogOpen(true); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedNote ? "Editează Sarcina" : "Adaugă Sarcină Nouă"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="font-body text-sm font-medium">Client *</label>
              <ClientSelect
                clients={clients}
                value={formData.client_id || "none"}
                onValueChange={(value, customName) => {
                  if (value === "__custom__") {
                    setFormData(prev => ({
                      ...prev,
                      client_id: "",
                      client_name: customName || "",
                    }));
                  } else {
                    const selected = clients.find(c => c.id === value);
                    setFormData(prev => ({
                      ...prev,
                      client_id: value === "none" ? "" : value,
                      client_name: selected ? (selected.nume || selected.denumire || "") : "",
                    }));
                  }
                }}
                placeholder="Selectează sau scrie nume client"
                emptyText="Niciunul"
                allowNone={false}
                allowCustom={true}
                customDisplayName={!formData.client_id && formData.client_name ? formData.client_name : ""}
                data-testid="select-client"
              />
            </div>

            <div className="space-y-2">
              <label className="font-body text-sm font-medium">Descriere (Opțional)</label>
              <Textarea
                placeholder="Detalii sarcină..."
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={3}
                data-testid="note-content-input"
              />
            </div>

            <div className="space-y-2">
              <label className="font-body text-sm font-medium">Dosar Asociat (Opțional)</label>
              <CaseSelect
                cases={cases}
                casesBC={casesBC}
                value={formData.case_id || "none"}
                onValueChange={(value) => setFormData(prev => ({ ...prev, case_id: value }))}
                placeholder="Selectează dosar"
                emptyText="Niciunul"
                allowNone={true}
                data-testid="select-case"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="font-body text-sm font-medium">Prioritate</label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">
                      <div className="flex items-center gap-2">
                        <ArrowUp className="w-4 h-4 text-red-600" />
                        Înaltă
                      </div>
                    </SelectItem>
                    <SelectItem value="normal">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-blue-600" />
                        Normală
                      </div>
                    </SelectItem>
                    <SelectItem value="low">
                      <div className="flex items-center gap-2">
                        <ArrowDown className="w-4 h-4 text-gray-600" />
                        Scăzută
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="font-body text-sm font-medium">Termen (opțional)</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="due-date-btn"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.due_date 
                        ? format(formData.due_date, "dd/MM/yyyy", { locale: ro })
                        : "Selectează"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.due_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, due_date: date }))}
                      locale={ro}
                    />
                    {formData.due_date && (
                      <div className="p-2 border-t">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setFormData(prev => ({ ...prev, due_date: null }))}
                        >
                          Șterge data
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleSubmit} data-testid="save-note-btn">
              {selectedNote ? "Salvează" : "Adaugă"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog with Comments */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="view-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Detalii Sarcină
            </DialogTitle>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-4">
              {/* Task Content */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="font-body font-medium">{selectedNote.client_name || "—"}</p>
                {selectedNote.content && (
                  <p className={`font-body text-sm text-muted-foreground mt-1 ${selectedNote.is_completed ? "line-through" : ""}`}>
                    {selectedNote.content}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Badge className={`${PRIORITY_CONFIG[selectedNote.priority]?.color} text-xs`}>
                    {PRIORITY_CONFIG[selectedNote.priority]?.label}
                  </Badge>
                  {selectedNote.due_date && (
                    <Badge variant="outline" className="text-xs">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {format(new Date(selectedNote.due_date), "dd/MM/yyyy", { locale: ro })}
                    </Badge>
                  )}
                  {selectedNote.is_completed && (
                    <Badge className="bg-green-500/10 text-green-600 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Completat
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Comments Section */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-semibold flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Notițe ({selectedNote.comments?.length || 0})
                </h4>
                
                {/* Existing Comments */}
                <ScrollArea className="h-[200px] pr-2">
                  {selectedNote.comments && selectedNote.comments.length > 0 ? (
                    <div className="space-y-2">
                      {selectedNote.comments.map((comment) => (
                        <div 
                          key={comment.id} 
                          className="p-3 bg-muted/50 rounded-lg group relative"
                        >
                          <p className="font-body text-sm pr-6">{comment.text}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(comment.created_at), "dd/MM/yyyy HH:mm", { locale: ro })}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <X className="w-3 h-3 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Nu există notițe. Adaugă prima notiță!
                    </p>
                  )}
                </ScrollArea>
                
                {/* Add Comment */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Scrie o notiță..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                    data-testid="new-comment-input"
                  />
                  <Button 
                    onClick={handleAddComment} 
                    disabled={!newComment.trim() || addingComment}
                    data-testid="add-comment-btn"
                  >
                    {addingComment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Închide
            </Button>
            <Button onClick={() => {
              setViewDialogOpen(false);
              handleOpenDialog(selectedNote);
            }}>
              <Pencil className="w-4 h-4 mr-2" />
              Editează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Șterge sarcina?</AlertDialogTitle>
            <AlertDialogDescription>
              Această acțiune nu poate fi anulată. Sarcina va fi ștearsă permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
